import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';


import {BasicPage} from 'src/components/basic-page';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContextProvider} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';

import {ConferenceTabs} from '../conference-tabs';
import {CONFERENCE_SUB_TABS} from '../constants';
import {DetailsView} from '../views/details.view';

import {useEditConference} from './useEditConference';

const SECTION_NAME = 'Conference Details';

type Props = {
    accountId: number,
    conferenceId: number,
};

const ConferenceDetails = ({accountId, conferenceId}: Props) => {
    const [busy, setBusy] = useState(false);
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);
    const {accountSubmit, accountFieldChange} = useEventTracking();
    const {hasAccess} = useAdminAccessRight();

    const {isCurrentChunkChanged, setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'conference-details-tab',
        onProceed: () => {
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        },
    });

    const {
        isLoading: isConferenceLoading,
        loadingError: conferenceLoadingError,
        isChanged: isConferenceChanged,
        data: conference,
        setData: setConference,
        saveChanges: saveConference,
        discardChanges: discardConference,
    } = useEditConference({accountId, conferenceId});

    useEffect(() => {
        setIsChunkChanged(isConferenceChanged);
    }, [isConferenceChanged, setIsChunkChanged]);

    const trackParameterChange = useCallback((parameterName: string) => {
        accountFieldChange({
            section: SECTION_NAME,
            viewType: 'Existed',
            parameterName,
        });
    }, [accountFieldChange]);

    const saveChanges = useCallback(async () => {
        accountSubmit({submit: true, place: SECTION_NAME});
        const calls = [];

        setBusy(true);
        if (isConferenceChanged) {
            calls.push(await saveConference());
        }
        setBusy(false);

        const errors = [...new Set(calls.filter(Boolean))];
        if (errors.length) {
            setErrorMessage(errors.join('\n'));
        } else {
            proceed();
        }
    }, [
        isConferenceChanged,
        saveConference,
        accountSubmit,
        proceed,
        setErrorMessage,
    ]);

    const discardChanges = useCallback(() => {
        discardConference();

        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
        accountSubmit({submit: false, place: SECTION_NAME});
    }, [discardConference, accountSubmit, setSuccessMessage]);

    const editable = useMemo(() => hasAccess(ACCESS_RIGHTS.CONFERENCE_EDIT), [hasAccess]);

    return (
        <>
            <ConferenceTabs accountId={accountId} activeSubTab={CONFERENCE_SUB_TABS.DETAILS.name}/>
            <BasicPage
                title="Details"
                buttonsBlock={editable ? (
                    <Group>
                        <Button
                            variant="Type.SECONDARY"
                            disabled={!isCurrentChunkChanged}
                            onClick={discardChanges}>
                            Discard Changes
                        </Button>
                        <Button
                            busy={busy}
                            variant="Type.PRIMARY"
                            disabled={!isCurrentChunkChanged}
                            onClick={saveChanges}>
                            Save Changes
                        </Button>
                    </Group>
                ) : null}>
                <div className="pd-l">
                    <DetailsView
                        readonly={!editable}
                        isLoading={isConferenceLoading}
                        loadingError={conferenceLoadingError}
                        data={conference}
                        setData={setConference}
                        trackParameterChange={trackParameterChange}
                    />
                </div>
            </BasicPage>
        </>
    );
};


export const ValidatedAConferenceDetails = (props: Props) => (
    <ValidationContextProvider>
        <ConferenceDetails {...props}/>
    </ValidationContextProvider>
);
